/**
 * goals.ts — Goal CRUD JS — Phase 163
 *
 * Modal: create / edit goal via constrained registry picker.
 * Inline delete: two-click (trash icon → confirm row) — no extra modal.
 * All mutations POST to signal URLs read from btn.dataset.url (never hardcode ?do=).
 * Bootstrap Modal + Toast imported directly — NOT window.bootstrap (silent no-op).
 * fetch POST uses new URLSearchParams() + Content-Type: application/x-www-form-urlencoded.
 */

import { Modal, Toast } from 'bootstrap';

// goalRegistry shape — populated by Goals/default.latte via {json_encode($registryOptions)|noescape}
interface RegistryOption {
    domain: string;
    metric: string;
    label: string;
    unit: string;
    direction: string;
}

declare const goalRegistry: RegistryOption[];

export default {

    _modal: null as Modal | null,
    _editMode: false,
    _inited: false,

    init(): void {
        // Idempotent init guard — prevent double-bind if init() is called twice
        if (this._inited) return;

        const modalEl = document.getElementById('goal-modal');
        if (!modalEl) return;  // not on the /goals page

        this._inited = true;
        this._modal = new Modal(modalEl);

        // Populate the goal-type <select> from goalRegistry (server-provided via Latte JSON)
        this._populateTypeSelect();

        // Bind "Add Goal" buttons (header + empty-state CTA)
        document.querySelectorAll<HTMLButtonElement>('.js-add-goal').forEach(btn => {
            btn.addEventListener('click', () => this._openCreate(btn));
        });

        // Bind "Edit" icon buttons
        document.addEventListener('click', (e: Event) => {
            const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.js-edit-goal');
            if (btn) this._openEdit(btn);
        });

        // Bind "Delete" icon buttons — delegate (cards may be inserted after init)
        document.addEventListener('click', (e: Event) => {
            const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.js-delete-goal');
            if (btn) this._startDeleteConfirm(btn);
        });

        // Dismiss coaching suggestion — fetch-POST → fade-remove, NO page reload (Pitfall 6)
        document.addEventListener('click', (e: Event) => {
            const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.js-dismiss-suggestion');
            if (btn) this._dismissSuggestion(btn);
        });

        // Goal-type picker change → update target label unit hint + default direction
        const typeSelect = modalEl.querySelector<HTMLSelectElement>('.js-goal-type-select');
        if (typeSelect) {
            typeSelect.addEventListener('change', () => this._onTypeChange());
        }

        // Modal save button
        const saveBtn = modalEl.querySelector<HTMLButtonElement>('.js-goal-save');
        if (saveBtn) {
            saveBtn.addEventListener('click', () => this._save());
        }

        // Reset modal on hidden (clear form + errors)
        modalEl.addEventListener('hidden.bs.modal', () => this._resetForm());
    },

    /** Populate the goal-type <select> from the registry array */
    _populateTypeSelect(): void {
        const modalEl = document.getElementById('goal-modal');
        if (!modalEl) return;
        const sel = modalEl.querySelector<HTMLSelectElement>('.js-goal-type-select');
        if (!sel || typeof goalRegistry === 'undefined') return;

        goalRegistry.forEach(opt => {
            const option = document.createElement('option');
            option.value = `${opt.domain}|${opt.metric}`;
            option.textContent = `${this._domainLabel(opt.domain)}: ${opt.label}`;
            option.dataset.unit = opt.unit;
            option.dataset.direction = opt.direction;
            sel.appendChild(option);
        });
    },

    _domainLabel(domain: string): string {
        const map: Record<string, string> = { health: 'Health', books: 'Books', alcohol: 'Alcohol' };
        return map[domain] ?? domain.charAt(0).toUpperCase() + domain.slice(1);
    },

    /** Open modal in create mode */
    _openCreate(btn: HTMLButtonElement): void {
        const modalEl = document.getElementById('goal-modal');
        if (!modalEl) return;

        this._editMode = false;

        // Reset + unlock all selects
        this._resetForm();
        this._setSelectsDisabled(false);

        // Update modal title + save button copy
        const titleEl = modalEl.querySelector<HTMLElement>('#goal-modal-label');
        const saveBtn = modalEl.querySelector<HTMLButtonElement>('.js-goal-save');
        if (titleEl) titleEl.textContent = 'Add Goal';
        if (saveBtn) saveBtn.textContent = 'Save Goal';

        this._modal?.show();
    },

    /** Open modal in edit mode — only target is editable */
    _openEdit(btn: HTMLButtonElement): void {
        const modalEl = document.getElementById('goal-modal');
        if (!modalEl) return;

        this._editMode = true;

        const goalId    = btn.dataset.goalId ?? '';
        const label     = btn.dataset.goalLabel ?? '';
        const target    = btn.dataset.goalTarget ?? '';
        const domain    = btn.dataset.goalDomain ?? '';
        const metric    = btn.dataset.goalMetric ?? '';
        const period    = btn.dataset.goalPeriod ?? '';
        const direction = btn.dataset.goalDirection ?? '';

        this._resetForm();

        // Populate fields
        const idInput = modalEl.querySelector<HTMLInputElement>('.js-goal-id');
        if (idInput) idInput.value = goalId;

        // Type select: show the matching option text but disable the select
        const typeSelect = modalEl.querySelector<HTMLSelectElement>('.js-goal-type-select');
        if (typeSelect) {
            const val = `${domain}|${metric}`;
            typeSelect.value = val;
            typeSelect.disabled = true;
        }

        // Target
        const targetInput = modalEl.querySelector<HTMLInputElement>('.js-goal-target');
        if (targetInput) targetInput.value = target;

        // Period (disabled)
        const periodSelect = modalEl.querySelector<HTMLSelectElement>('.js-goal-period');
        if (periodSelect) { periodSelect.value = period; periodSelect.disabled = true; }

        // Direction (disabled)
        const dirSelect = modalEl.querySelector<HTMLSelectElement>('.js-goal-direction');
        if (dirSelect) { dirSelect.value = direction; dirSelect.disabled = true; }

        // Hidden fallbacks for POST in edit mode (disabled selects are not submitted)
        const domainHidden = modalEl.querySelector<HTMLInputElement>('.js-goal-domain-hidden');
        const metricHidden = modalEl.querySelector<HTMLInputElement>('.js-goal-metric-hidden');
        const periodHidden = modalEl.querySelector<HTMLInputElement>('.js-goal-period-hidden');
        const dirHidden    = modalEl.querySelector<HTMLInputElement>('.js-goal-direction-hidden');
        if (domainHidden) domainHidden.value = domain;
        if (metricHidden) metricHidden.value = metric;
        if (periodHidden) periodHidden.value = period;
        if (dirHidden)    dirHidden.value    = direction;

        // Update unit label
        this._updateTargetLabel();

        // Update modal title + save button copy
        const titleEl = modalEl.querySelector<HTMLElement>('#goal-modal-label');
        const saveBtn = modalEl.querySelector<HTMLButtonElement>('.js-goal-save');
        if (titleEl) titleEl.textContent = `Edit Goal: ${label}`;
        if (saveBtn) saveBtn.textContent = 'Update Goal';

        this._modal?.show();
    },

    _setSelectsDisabled(disabled: boolean): void {
        const modalEl = document.getElementById('goal-modal');
        if (!modalEl) return;
        ['js-goal-type-select', 'js-goal-period', 'js-goal-direction'].forEach(cls => {
            const el = modalEl.querySelector<HTMLSelectElement>(`.${cls}`);
            if (el) el.disabled = disabled;
        });
    },

    _onTypeChange(): void {
        this._updateTargetLabel();
        // Pre-select default direction from registry option
        const modalEl = document.getElementById('goal-modal');
        if (!modalEl) return;
        const typeSelect = modalEl.querySelector<HTMLSelectElement>('.js-goal-type-select');
        if (!typeSelect) return;
        const opt = typeSelect.selectedOptions[0];
        if (!opt) return;
        const dir = opt.dataset.direction ?? 'at_least';
        const dirSelect = modalEl.querySelector<HTMLSelectElement>('.js-goal-direction');
        if (dirSelect && !dirSelect.disabled) dirSelect.value = dir;
    },

    _updateTargetLabel(): void {
        const modalEl = document.getElementById('goal-modal');
        if (!modalEl) return;
        const typeSelect = modalEl.querySelector<HTMLSelectElement>('.js-goal-type-select');
        const labelEl    = modalEl.querySelector<HTMLElement>('.js-goal-target-label');
        if (!typeSelect || !labelEl) return;
        const opt  = typeSelect.selectedOptions[0];
        const unit = opt?.dataset.unit ?? '';
        labelEl.textContent = unit ? `Target (${unit})` : 'Target';
    },

    /** Client-side validation + POST save */
    _save(): void {
        const modalEl = document.getElementById('goal-modal');
        if (!modalEl) return;

        const errorEl  = modalEl.querySelector<HTMLElement>('.js-goal-error');
        const spinnerEl = modalEl.querySelector<HTMLElement>('.js-goal-spinner');
        const saveBtn  = modalEl.querySelector<HTMLButtonElement>('.js-goal-save');
        const formEl   = modalEl.querySelector<HTMLElement>('.js-goal-form');

        const showError = (msg: string) => {
            if (errorEl) { errorEl.textContent = msg; errorEl.classList.remove('d-none'); }
        };
        const clearError = () => {
            if (errorEl) { errorEl.textContent = ''; errorEl.classList.add('d-none'); }
        };

        clearError();

        // Client-side validation
        const typeSelect   = modalEl.querySelector<HTMLSelectElement>('.js-goal-type-select');
        const targetInput  = modalEl.querySelector<HTMLInputElement>('.js-goal-target');
        const typeVal = typeSelect?.value ?? '';
        const targetVal = parseFloat(targetInput?.value ?? '0');

        if (!typeVal) {
            showError('Please select a goal type.');
            return;
        }
        if (!targetInput?.value || isNaN(targetVal) || targetVal <= 0) {
            showError('Target must be greater than 0.');
            return;
        }

        // Build POST body
        const body = new URLSearchParams();
        const goalId = modalEl.querySelector<HTMLInputElement>('.js-goal-id')?.value ?? '';

        if (this._editMode) {
            // Edit mode: id + target
            body.set('id', goalId);
            body.set('target', String(targetVal));
        } else {
            // Create mode: full field set
            const [domain, metric] = typeVal.split('|');
            const periodSelect  = modalEl.querySelector<HTMLSelectElement>('.js-goal-period');
            const dirSelect     = modalEl.querySelector<HTMLSelectElement>('.js-goal-direction');

            body.set('domain',    domain ?? '');
            body.set('metric',    metric ?? '');
            body.set('target',    String(targetVal));
            body.set('period',    periodSelect?.value ?? 'monthly');
            body.set('direction', dirSelect?.value ?? 'at_least');
            body.set('label',     '');  // model uses registry label if blank
        }

        // Determine signal URL
        const url = this._editMode
            ? (modalEl.dataset.editUrl ?? '')
            : (modalEl.dataset.createUrl ?? '');

        if (!url) { showError('Signal URL not configured.'); return; }

        // Spinner on, save button disabled
        if (spinnerEl) spinnerEl.classList.remove('d-none');
        if (formEl)    formEl.classList.add('d-none');
        if (saveBtn)   { saveBtn.disabled = true; saveBtn.textContent = 'Saving…'; }

        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            if (data.success) {
                this._modal?.hide();
                if (this._editMode) {
                    // Reload so the server-rendered SVG ring re-computes its fill against the
                    // new target (WR-05: patching only the caption left the ring arc stale).
                    this._updateCardTarget(goalId, String(targetVal));
                    this._showToast('Goal updated.', 'success');
                    setTimeout(() => window.location.reload(), 700);
                } else {
                    // Reload page to show new card (simplest correct approach — avoids
                    // complex DOM insertion with a server-rendered ring partial)
                    this._showToast('Goal created.', 'success');
                    setTimeout(() => window.location.reload(), 700);
                }
            } else {
                if (spinnerEl) spinnerEl.classList.add('d-none');
                if (formEl)    formEl.classList.remove('d-none');
                if (saveBtn)   { saveBtn.disabled = false; saveBtn.textContent = this._editMode ? 'Update Goal' : 'Save Goal'; }
                showError(data.message ?? 'Could not save goal. Please try again.');
            }
        })
        .catch(() => {
            if (spinnerEl) spinnerEl.classList.add('d-none');
            if (formEl)    formEl.classList.remove('d-none');
            if (saveBtn)   { saveBtn.disabled = false; saveBtn.textContent = this._editMode ? 'Update Goal' : 'Save Goal'; }
            showError('Could not save goal. Please try again.');
        });
    },

    /** Inline two-click delete: swap CRUD buttons → confirm row */
    _startDeleteConfirm(deleteBtn: HTMLButtonElement): void {
        const col = deleteBtn.closest<HTMLElement>('.col');
        if (!col) return;
        const btnRow = col.querySelector<HTMLElement>('.js-goal-btn-row');
        if (!btnRow) return;

        const goalId    = deleteBtn.dataset.goalId ?? '';
        const goalLabel = deleteBtn.dataset.goalLabel ?? '';
        const deleteUrl = deleteBtn.dataset.url ?? '';

        // Build confirm row
        const confirmRow = document.createElement('div');
        confirmRow.className = 'js-goal-confirm-row d-flex flex-column align-items-center gap-1 mt-auto';
        confirmRow.innerHTML = `
            <span class="text-danger small">Delete &ldquo;${this._esc(goalLabel)}&rdquo;?</span>
            <div class="d-flex gap-2">
                <button type="button" class="btn btn-sm btn-danger js-confirm-delete"
                        style="min-height:44px">Confirm</button>
                <button type="button" class="btn btn-sm btn-secondary js-cancel-delete"
                        style="min-height:44px">Cancel</button>
            </div>
        `;

        // Swap
        btnRow.classList.add('d-none');
        btnRow.insertAdjacentElement('afterend', confirmRow);

        // Confirm
        confirmRow.querySelector<HTMLButtonElement>('.js-confirm-delete')?.addEventListener('click', () => {
            this._executeDelete(goalId, deleteUrl, col, confirmRow, btnRow);
        });

        // Cancel
        confirmRow.querySelector<HTMLButtonElement>('.js-cancel-delete')?.addEventListener('click', () => {
            confirmRow.remove();
            btnRow.classList.remove('d-none');
        });
    },

    _executeDelete(goalId: string, url: string, col: HTMLElement, confirmRow: HTMLElement, btnRow: HTMLElement): void {
        const body = new URLSearchParams();
        body.set('id', goalId);

        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            if (data.success) {
                // Fade card out then remove
                col.style.transition = 'opacity 0.3s';
                col.style.opacity = '0';
                setTimeout(() => col.remove(), 350);
                this._showToast('Goal deleted.', 'success');
            } else {
                // Show error below confirm row
                const errEl = document.createElement('span');
                errEl.className = 'text-danger small';
                errEl.textContent = data.message ?? 'Could not delete goal. Try again.';
                confirmRow.appendChild(errEl);
            }
        })
        .catch(() => {
            const errEl = document.createElement('span');
            errEl.className = 'text-danger small';
            errEl.textContent = 'Could not delete goal. Try again.';
            confirmRow.appendChild(errEl);
        });
    },

    /** Update card target caption after a successful edit */
    _updateCardTarget(goalId: string, target: string): void {
        const card = document.querySelector<HTMLElement>(`.col [data-goal-id="${goalId}"]`);
        if (!card) return;
        // Update the edit button's data-goal-target so next edit pre-fills correctly
        const editBtn = card.querySelector<HTMLButtonElement>('.js-edit-goal');
        if (editBtn) editBtn.dataset.goalTarget = target;
        // Update the visible caption (rough update — ring still shows old pct until reload)
        const caption = card.querySelector<HTMLElement>('.card-body p.text-muted.small');
        if (caption) {
            // Keep existing unit — read from edit button data
            const metric = editBtn?.dataset.goalMetric ?? '';
            const unitMap: Record<string, string> = { weekly_km: 'km', books_year: 'books', dry_days: 'days' };
            const unit = unitMap[metric] ?? '';
            caption.textContent = `— / ${target} ${unit}`;
        }
    },

    /**
     * Dismiss a coaching suggestion via fetch-POST → fade card → remove from DOM.
     * Mirror of _executeDelete() shape — but NO toast and NO page reload (Pitfall 6:
     * dismiss is pure state; ring re-compute not needed here).
     */
    _dismissSuggestion(btn: HTMLButtonElement): void {
        const id  = btn.dataset.suggestionId ?? '';
        const url = btn.dataset.url ?? '';
        if (!id || !url) return;

        const card = btn.closest<HTMLElement>('.js-coaching-card');

        const body = new URLSearchParams();
        body.set('id', id);

        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            if (data.success && card) {
                // Fade + remove — no page reload, no toast (UI-SPEC: ambient dismiss)
                card.style.transition = 'opacity 0.3s';
                card.style.opacity = '0';
                setTimeout(() => card.remove(), 350);
            }
            // On failure: silent — card stays; user will see it on next page load
        })
        .catch(() => { /* silent fail — card stays */ });
    },

    _showToast(msg: string, type: 'success' | 'error'): void {
        const toastEl = document.getElementById('goal-toast');
        if (!toastEl) return;
        toastEl.classList.remove('bg-success', 'bg-danger', 'text-white');
        toastEl.classList.add(type === 'success' ? 'bg-success' : 'bg-danger', 'text-white');
        const msgEl = toastEl.querySelector<HTMLElement>('.js-goal-toast-msg');
        if (msgEl) msgEl.textContent = msg;
        new Toast(toastEl, { delay: 3000 }).show();
    },

    _resetForm(): void {
        const modalEl = document.getElementById('goal-modal');
        if (!modalEl) return;
        const formEl = modalEl.querySelector<HTMLElement>('.js-goal-form');
        if (formEl) formEl.classList.remove('d-none');

        const idInput     = modalEl.querySelector<HTMLInputElement>('.js-goal-id');
        const typeSelect  = modalEl.querySelector<HTMLSelectElement>('.js-goal-type-select');
        const targetInput = modalEl.querySelector<HTMLInputElement>('.js-goal-target');
        const errorEl     = modalEl.querySelector<HTMLElement>('.js-goal-error');
        const spinnerEl   = modalEl.querySelector<HTMLElement>('.js-goal-spinner');
        const saveBtn     = modalEl.querySelector<HTMLButtonElement>('.js-goal-save');
        const labelEl     = modalEl.querySelector<HTMLElement>('.js-goal-target-label');

        if (idInput)     idInput.value = '';
        if (typeSelect)  typeSelect.value = '';
        if (targetInput) targetInput.value = '';
        if (errorEl)     { errorEl.textContent = ''; errorEl.classList.add('d-none'); }
        if (spinnerEl)   spinnerEl.classList.add('d-none');
        if (saveBtn)     { saveBtn.disabled = false; saveBtn.textContent = 'Save Goal'; }
        if (labelEl)     labelEl.textContent = 'Target';

        // Clear hidden inputs
        const domainH = modalEl.querySelector<HTMLInputElement>('.js-goal-domain-hidden');
        const metricH = modalEl.querySelector<HTMLInputElement>('.js-goal-metric-hidden');
        const periodH = modalEl.querySelector<HTMLInputElement>('.js-goal-period-hidden');
        const dirH    = modalEl.querySelector<HTMLInputElement>('.js-goal-direction-hidden');
        if (domainH) domainH.value = '';
        if (metricH) metricH.value = '';
        if (periodH) periodH.value = '';
        if (dirH)    dirH.value    = '';
    },

    /** Simple HTML escape for user-supplied strings in innerHTML */
    _esc(s: string): string {
        return s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    },
};
